import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';

export const OngoingSupportForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    currentSystems: '',
    supportNeeds: '',
    monthlyBudget: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/ongoing-support/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl glass rounded-2xl p-8 max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#A1A1AA] hover:text-[#F4F4F5] transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {!submitted ? (
          <>
            <h2 className="text-3xl font-headline font-bold text-[#F4F4F5] mb-2">
              Get Ongoing AI Support
            </h2>
            <p className="text-[#A1A1AA] font-body mb-6">
              Tell us about your current AI systems and we will create a custom support plan for you.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599]"
                />
              </div>

              <div>
                <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                  Current AI Systems/Tools *
                </label>
                <textarea
                  required
                  rows={3}
                  placeholder="Tell us what AI systems or automation you currently have in place..."
                  value={formData.currentSystems}
                  onChange={(e) => setFormData({...formData, currentSystems: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599] resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                  What support do you need? *
                </label>
                <textarea
                  required
                  rows={4}
                  placeholder="Describe your support needs (monitoring, optimization, troubleshooting, expansion, etc.)"
                  value={formData.supportNeeds}
                  onChange={(e) => setFormData({...formData, supportNeeds: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599] resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-body font-semibold text-[#F4F4F5] mb-2">
                  Monthly Budget Range *
                </label>
                <select
                  required
                  value={formData.monthlyBudget}
                  onChange={(e) => setFormData({...formData, monthlyBudget: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0F0F13] border border-white/10 rounded-lg text-[#F4F4F5] font-body focus:outline-none focus:border-[#00E599]"
                >
                  <option value="">Select budget range</option>
                  <option value="1k-3k">$1K - $3K/month</option>
                  <option value="3k-5k">$3K - $5K/month</option>
                  <option value="5k-10k">$5K - $10K/month</option>
                  <option value="10k-plus">$10K+/month</option>
                </select>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#00E599] hover:bg-[#00C985] text-[#050505] px-8 py-4 text-base font-body font-bold transition-all duration-300 hover:shadow-xl hover:shadow-[#00E599]/30 rounded-lg"
              >
                {isSubmitting ? 'Submitting...' : 'Request Support Plan'}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-[#00E599] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-[#050505]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-headline font-bold text-[#F4F4F5] mb-2">
              Support Request Received!
            </h3>
            <p className="text-[#A1A1AA] font-body">
              We will review your systems and create a custom support plan. Expect to hear from us within 24 hours.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OngoingSupportForm;
